import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useSEO } from '../../utils/seo';
import Icon from '../../components/base/Icon';
import { blogArticles, categories } from '../../mocks/blogArticles';

export default function BlogPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useSEO({
    title: 'Boise Home Improvement Blog | Fence, Handyman & Remodel Tips',
    description:
      'Expert tips on fence installation cost Boise, handyman services Boise Idaho, home remodel ROI Treasure Valley, fix and flip contractor Boise. Your trusted Boise home improvement blog.',
    keywords:
      'fence installation cost Boise, handyman services Boise Idaho, home remodel ROI Treasure Valley, fix and flip contractor Boise, Boise home improvement blog, residential remodeling Idaho',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/blog`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Boise Home Improvement Blog | Fix Right Services',
      description: 'Expert tips on fence installation cost Boise, handyman services Boise Idaho, home remodel ROI Treasure Valley, fix and flip contractor Boise.',
      url: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/blog`,
      inLanguage: 'en-US',
      publisher: {
        '@type': 'Organization',
        name: 'Fix Right Services',
        url: import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com',
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/7bcdcf10e320261581936ea1de735eda.jpeg',
        },
        sameAs: [
          'https://www.facebook.com/profile.php?id=61582658047880',
          'https://www.instagram.com/fixright_services/',
        ],
      },
      blogPost: blogArticles.map((article) => ({
        '@type': 'BlogPosting',
        headline: article.title,
        datePublished: article.date,
        url: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/blog/${article.slug}`,
        keywords: [article.primaryKeyword, ...article.secondaryKeywords].join(', '),
      })),
    },
  });

  const filteredArticles = blogArticles.filter((article) => {
    const matchesSearch =
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.primaryKeyword.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === 'All' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/7bcdcf10e320261581936ea1de735eda.jpeg"
                alt="Fix Right Services Logo"
                width="180"
                height="56"
                className="h-14 w-auto"
                loading="eager"
              />
            </Link>
            <a
              href="tel:+12082046331"
              className="flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              aria-label="Call Fix Right Services"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <Icon name="phone" className="w-4 h-4" />
              </span>
              <span className="hidden sm:inline">(208) 204-6331</span>
              <span className="sm:hidden">Call</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Tips &amp; Advice
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Expert insights on fencing, handyman services, and home remodeling from the
            Fix Right Services team
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-6">
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <Icon name="magnifying-glass" className="w-4 h-4 text-gray-400" />
              </span>
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm"
                aria-label="Search articles"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div
            className="flex flex-wrap justify-center gap-3"
            role="group"
            aria-label="Filter by category"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-[#B45309] text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" aria-label="Blog articles">
        <div className="max-w-7xl mx-auto">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-12">
              <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Icon name="file-circle-question" className="w-16 h-16 text-gray-300" />
              </span>
              <p className="text-xl text-gray-500">
                No articles found matching your search.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <article
                  key={article.id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                >
                  <div className="h-56 overflow-hidden relative">
                    <img
                      src={article.image}
                      alt={article.title}
                      width="800"
                      height="500"
                      className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      decoding="async"
                    />
                    {article.isFeatured && (
                      <span className="absolute top-3 right-3 bg-[#B45309] text-white text-xs font-bold px-3 py-1 rounded-full">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-semibold text-[#92400E] bg-[#B45309]/10 px-3 py-1 rounded-full">
                        {article.category}
                      </span>
                      <span className="text-xs text-gray-500">{article.date}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 mb-3 text-sm">{article.excerpt}</p>
                    <span className="sr-only">Primary keyword: {article.primaryKeyword}</span>
                    <span className="inline-block text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded mb-4">
                      {article.primaryKeyword}
                    </span>
                    <Link
                      to={`/blog/${article.slug}`}
                      className="inline-flex items-center gap-2 text-[#B45309] hover:text-[#92400E] font-semibold text-sm cursor-pointer"
                    >
                      Read More
                      <Icon name="arrow-right" className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-16 px-4 sm:px-6 lg:px-8 bg-[#B45309]"
        aria-label="Call to action"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Need Help With Your Project?
          </h2>
          <p className="text-xl text-white/95 mb-8">
            Our team is ready to help with your fence, handyman, or remodeling needs
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+12082046331"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-[#B45309] px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <Icon name="phone" className="w-4 h-4" />
              </span>
              Call Now
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <Icon name="file-lines" className="w-4 h-4" />
              </span>
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img
                src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp"
                alt="Fix Right Services"
                width="180"
                height="48"
                className="h-12 w-auto mb-4"
                loading="lazy"
              />
              <p className="text-gray-400 text-sm">
                Boise&apos;s trusted experts for fence installation, handyman services,
                and home remodels.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Services</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Fence Installation &amp; Repair</li>
                <li>Handyman Services</li>
                <li>Home Remodels</li>
                <li>Kitchen Renovations</li>
                <li>Bathroom Remodeling</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link
                    to="/blog"
                    className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                  >
                    Tips &amp; Advice
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Contact</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <Icon name="phone" className="w-3.5 h-3.5 text-[#F59E0B]" />
                  </span>
                  <a href="tel:+12082046331" className="hover:text-white transition-colors cursor-pointer">
                    (208) 204-6331
                  </a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <Icon name="envelope" className="w-3.5 h-3.5 text-[#F59E0B]" />
                  </span>
                  <a href="mailto:Southernkole@gmail.com" className="hover:text-white transition-colors cursor-pointer">
                    Southernkole@gmail.com
                  </a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <Icon name="location-dot" className="w-3.5 h-3.5 text-[#F59E0B]" />
                  </span>
                  <span>Serving Boise, Meridian, Nampa &amp; Caldwell</span>
                </li>
              </ul>
              <div className="flex gap-3 mt-4">
                <a
                  href="https://www.facebook.com/profile.php?id=61582658047880"
                  aria-label="Visit our Facebook page"
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                >
                  <Icon name="facebook" className="w-5 h-5" />
                </a>
                <a
                  href="https://www.instagram.com/fixright_services/"
                  aria-label="Visit our Instagram page"
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                >
                  <Icon name="instagram" className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors cursor-pointer"
            >
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}